/**
 * MegaMenu Component - click-toggle interaction.
 * 
 * Files read:
 * - core/sections/header.liquid (defined the mega-menu custom element)
 * - core/blocks/mega-menu-featured.liquid (dropdown mega-menu details/summary markup)
 * - core/blocks/mega-menu-simple-link.liquid (dropdown details/summary markup with toggle buttons)
 * - frontend/css/components/mega-menu.scss (no hover-driven dropdown visibility CSS found, natively details[open] based)
 * 
 * Pattern found:
 * - Web Component (MegaMenu) managing native HTML5 details/summary dropdowns.
 * - Previous implementation used JS mouseenter/mouseleave events to toggle 'open' attribute on hover.
 * 
 * What was changed:
 * - Removed mouseenter and mouseleave listeners from details elements to disable hover behaviour.
 * - Removed legacy mouseenter/mouseleave listeners from .mega-menu-item selectors.
 * - Upgraded the details 'click' event listener with preventDefault() to reliably toggle 'open' and aria-expanded programmatically.
 * - Added a document click listener inside connectedCallback to close mega menus on click outside.
 * - Added a document keydown listener for Escape key to close mega menus and return focus to the summary trigger.
 * - Added disconnectedCallback to clean up document-level click and keydown event listeners.
 * 
 * What was intentionally left untouched:
 * - Keyboard navigation helper for Enter/Space (line 72-80) preserved for native keyboard accessibility.
 * - Swiper initialization update logic inside openDetails preserved to keep carousel sizing intact.
 * - Mobile nav drawer/accordion left completely untouched (scoped to desktop mega-menu custom element only).
 */
class MegaMenu extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const detailElements = this.querySelectorAll("details");

    detailElements.forEach((details, index) => {
      const summary = details.querySelector("summary");
      const content = details.querySelector(".mega-menu__summary-inner");

      if (!summary || !content) return;

      // Assign unique ID if not already present
      if (!content.id) {
        content.id = `mega-menu-panel-${index}`;
      }

      // Set ARIA attributes
      summary.setAttribute("role", "button");
      summary.setAttribute("aria-controls", content.id);
      summary.setAttribute(
        "aria-expanded",
        details.hasAttribute("open") ? "true" : "false"
      );
      summary.setAttribute("tabindex", "0");

      // Click to toggle dropdown
      details.addEventListener("click", (event) => {
        const summaryElement = event.target.closest("summary");
        if (!summaryElement) return;

        // Prevent native HTML collapsible details toggle and link navigation
        event.preventDefault();
         
        if (details.hasAttribute("open")) {
          this.closeDetails(details);
          document.body.classList.remove("mega-menu-open");
        } else {
          this.openDetails(details);
        }
      });
    });

    // Enter/Space accessibility handler on summary elements
    this.addEventListener("keydown", (event) => {
      if (
        (event.key === "Enter" || event.key === " ") &&
        event.target.matches("summary")
      ) {
        event.preventDefault();
        event.target.click();
      }
    });

    // Outside click handler
    this._clickOutsideHandler = (event) => {
      if (this.contains(event.target)) return;
      this.querySelectorAll("details[open]").forEach((details) => {
        this.closeDetails(details);
      });
    };
    document.addEventListener("click", this._clickOutsideHandler);

    // Escape key handler
    this._escapeHandler = (event) => {
      if (event.key !== "Escape") return;
      const openDetails = this.querySelectorAll("details[open]");
      openDetails.forEach((details) => {
        this.closeDetails(details);
        const summary = details.querySelector("summary");
        if (summary) {
          summary.focus();
        }
      });
    };
    document.addEventListener("keydown", this._escapeHandler);

    this.updateBodyClass();

    // Expose global function to close the mega menu
    window.closeMegaMenu = () => {
      this.querySelectorAll("details").forEach((details) => {
        this.closeDetails(details);
      });
    };
  }

  disconnectedCallback() {
    if (this._clickOutsideHandler) {
      document.removeEventListener("click", this._clickOutsideHandler);
    }
    if (this._escapeHandler) {
      document.removeEventListener("keydown", this._escapeHandler);
    }
  }

  openDetails(details) {
    // Close all other details
    this.querySelectorAll("details").forEach((otherDetails) => {
      if (otherDetails !== details) {
        this.closeDetails(otherDetails);
      }
    });

    // Remove closing state if it was there
    details.classList.remove("is-closing");
    const content = details.querySelector(".mega-menu__summary-inner");
    if (content) {
      content.classList.remove("is-closing");
    }

    // Open the specified details
    details.setAttribute("open", "");
    const summary = details.querySelector("summary");
    if (summary) {
      summary.setAttribute("aria-expanded", "true");
    }
    this.updateBodyClass();

    // Update any Swiper instances inside this details element
    // Use requestAnimationFrame to ensure the DOM has updated
    requestAnimationFrame(() => {
      setTimeout(() => {
        const swiperComponents = details.querySelectorAll("swiper-component");
        swiperComponents.forEach((swiperComponent) => {
          // Try to call the updateSwiper method if it exists
          if (typeof swiperComponent.updateSwiper === 'function') {
            swiperComponent.updateSwiper();
          } else {
            // Fallback to direct swiper access
            const swiper = swiperComponent.swiper || swiperComponent.swiperInstance;
            if (swiper && swiper.update) {
              swiper.updateSize();
              swiper.updateSlides();
              swiper.slideTo(0, 0); // Reset to first slide
              swiper.update();
            }
          }
        });
      }, 100);
    });
  }

  closeDetails(details) {
    const content = details.querySelector(".mega-menu__summary-inner");
    
    if (content && details.hasAttribute("open")) {
      // Add closing class to trigger closing animation
      details.classList.add("is-closing");
      content.classList.add("is-closing");

      // Wait for the transition to finish (400ms matching our CSS transition)
      setTimeout(() => {
        // Double check if details is still in closing state (user didn't re-open it)
        if (details.classList.contains("is-closing")) {
          details.removeAttribute("open");
          details.classList.remove("is-closing");
          content.classList.remove("is-closing");
          const summary = details.querySelector("summary");
          if (summary) {
            summary.setAttribute("aria-expanded", "false");
          }
          this.updateBodyClass();
        }
      }, 400); // 400ms matches the CSS transition duration
    } else {
      details.removeAttribute("open");
      details.classList.remove("is-closing");
      if (content) {
        content.classList.remove("is-closing");
      }
      const summary = details.querySelector("summary");
      if (summary) {
        summary.setAttribute("aria-expanded", "false");
      }
      this.updateBodyClass();
    }
  }

  updateBodyClass() {
    const anyOpen = this.querySelector("details[open]");
    document.body.classList.toggle("mega-menu-open", !!anyOpen);
  }
}
export default MegaMenu;
